import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Icon from './Icon';
import NotificationRow from './NotificationRow';
import { NAV, PAGE_META } from '../data/mock';
import { useAuth } from '../context/AuthContext';
import { UnsavedChangesContext } from '../context/UnsavedChanges';
import {
  Notification,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api/notifications';
import { resolveNotificationPath } from '../api/notificationLinks';
import { NewCounts, getNewCounts, onItemSeen } from '../api/newCounts';
import logoMark from '../assets/logo-mark.png';

const initials = (name: string): string =>
  name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

/** Which count each sidebar item shows. */
const NEW_COUNT_FOR_NAV: Record<string, keyof NewCounts> = {
  leads: 'inquiries',
  applications: 'applications',
  servicerequests: 'maintenance',
  orders: 'legal',
};

const AdminLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const meta =
    PAGE_META[location.pathname] ??
    (/^\/admin\/listings\/\d+$/.test(location.pathname)
      ? { title: 'Listing Details', subtitle: 'Full details for this property' }
      : /^\/admin\/applications\/\d+$/.test(location.pathname)
      ? { title: 'Rental Application Details', subtitle: 'Everything the applicant submitted — details, documents and payment' }
      : /^\/admin\/service-requests\/\d+$/.test(location.pathname)
      ? { title: 'Service Order Details', subtitle: 'Maintenance service order — what the customer asked for and its progress' }
      : /^\/admin\/orders\/\d+$/.test(location.pathname)
      ? { title: 'Legal Services Request Details', subtitle: 'Legal service booking — customer, payment and progress' }
      : /^\/admin\/legal\/\d+$/.test(location.pathname)
      ? { title: 'Legal Service Details', subtitle: 'Full details for this legal service' }
      : /^\/admin\/users\/\d+$/.test(location.pathname)
      ? { title: 'User Details', subtitle: 'Profile and all app activity for this user' }
      : /^\/admin\/agents\/\d+$/.test(location.pathname)
      ? { title: 'Merchant Details', subtitle: 'Profile, subscription and listings for this merchant' }
      : { title: 'Admin', subtitle: '' });

  const handleLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  // Notification bell: unread dot + a dropdown panel anchored below the bell.
  const [notifOpen, setNotifOpen] = useState<boolean>(false);
  const [notifLoading, setNotifLoading] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const notifRef = useRef<HTMLDivElement | null>(null);

  // Close the panel on outside click or Escape.
  useEffect(() => {
    if (!notifOpen) return;
    const onDown = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNotifOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [notifOpen]);

  useEffect(() => {
    let cancelled = false;
    listNotifications({ limit: 1 })
      .then((res) => {
        if (!cancelled) setUnreadCount(res.unreadCount);
      })
      .catch(() => {
        if (!cancelled) setUnreadCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Sidebar counts of items no admin has opened yet: refreshed on navigation,
  // when one gets opened, and every minute so new ones show up.
  const [newCounts, setNewCounts] = useState<NewCounts | null>(null);
  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      getNewCounts()
        .then((counts) => {
          if (!cancelled) setNewCounts(counts);
        })
        .catch(() => {
          /* keep the last known counts */
        });
    };
    refresh();
    const timer = window.setInterval(refresh, 60000);
    const unsubscribe = onItemSeen(refresh);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      unsubscribe();
    };
  }, [location.pathname]);

  const openNotifications = async () => {
    if (notifOpen) {
      setNotifOpen(false);
      return;
    }
    setNotifOpen(true);
    setNotifLoading(true);
    try {
      const res = await listNotifications({ limit: 30 });
      setNotifications(res.notifications);
      setUnreadCount(res.unreadCount);
    } catch {
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      /* leave state as-is; user can retry */
    }
  };

  /*
   * Open what a notification is about. The dropdown lives inside notifRef, so
   * the outside-click handler never fires for it — close it here or it stays
   * open over the destination page.
   */
  const handleNotificationClick = (n: Notification, path: string) => {
    setNotifOpen(false);
    if (!n.isRead) {
      setNotifications((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      markNotificationRead(n.id).catch(() => {
        /* badge re-syncs on the next fetch */
      });
    }
    navigate(path);
  };

  // Sidebar groups (Master Management): open unless the admin collapsed them.
  const GROUPS_KEY = 'rentagade.admin.navGroups';
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(window.localStorage.getItem(GROUPS_KEY) || '{}');
    } catch {
      return {};
    }
  });
  const isGroupOpen = (id: string): boolean => openGroups[id] !== false;
  const toggleGroup = (id: string) =>
    setOpenGroups((prev) => {
      const next = { ...prev, [id]: !(prev[id] !== false) };
      try {
        window.localStorage.setItem(GROUPS_KEY, JSON.stringify(next));
      } catch {
        /* private mode — the choice just isn't remembered */
      }
      return next;
    });

  // Pages with unsaved edits flag it here; the sidebar asks before leaving.
  const unsavedRef = useRef(false);
  const unsavedValue = useMemo(
    () => ({
      setUnsaved: (dirty: boolean) => {
        unsavedRef.current = dirty;
      },
    }),
    []
  );
  const confirmLeave = (e: React.MouseEvent, path: string) => {
    if (path === location.pathname || !unsavedRef.current) return;
    if (!window.confirm('You have unsaved changes on this page. Leave without saving them?')) {
      e.preventDefault();
    }
  };

  // A nav item owns its sub-pages too, so "Buy / Rent Listing" stays highlighted
  // on /admin/listings/new and /admin/listings/:id.
  const isItemActive = (path: string): boolean => {
    const current = location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/admin') return current === '/admin';
    return current === path || current.startsWith(path + '/');
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <aside className="w-[260px] bg-white border-r border-g100 flex flex-col flex-shrink-0 shadow-sidebar">
        <div className="px-6 pt-6 pb-5 border-b border-g100 flex items-center gap-3">
          <img
            src={logoMark}
            alt="Rentagade"
            className="w-10 h-10 rounded-full object-contain flex-shrink-0"
          />
          <div>
            <div className="text-[18px] font-extrabold text-ink tracking-tight leading-none">
              Rentagade
            </div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-g400 mt-1">
              Admin Console
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 flex flex-col gap-0.5 overflow-y-auto">
          {NAV.map((n) => {
            // Items of a collapsed group are hidden.
            if (n.group && !isGroupOpen(n.group)) return null;

            if (n.header) {
              const open = isGroupOpen(n.id);
              // Highlight a collapsed group that holds the current page.
              const holdsActive =
                !open && NAV.some((c) => c.group === n.id && c.path && isItemActive(c.path));
              return (
                <React.Fragment key={n.id}>
                  {n.divider && <div className="h-px bg-g100 mx-4 my-2" />}
                  <button
                    type="button"
                    onClick={() => toggleGroup(n.id)}
                    aria-expanded={open}
                    className={
                      'relative flex items-center gap-3 w-full px-4 py-[11px] rounded-xl text-sm text-left transition-all' +
                      (holdsActive ? ' bg-brand-light text-brand font-bold' : ' text-g700 font-medium hover:bg-g50')
                    }
                  >
                    <Icon name={n.icon} size={20} color={holdsActive ? 'var(--brand)' : 'currentColor'} />
                    <span className="flex-1">{n.label}</span>
                    <span
                      className="inline-flex"
                      style={{ transition: 'transform .2s ease', transform: open ? 'rotate(180deg)' : 'none' }}
                    >
                      <Icon name="chevron-down" size={16} color="currentColor" />
                    </span>
                  </button>
                </React.Fragment>
              );
            }

            const path = n.path || '/admin';
            const isActive = isItemActive(path);
            const newCount = NEW_COUNT_FOR_NAV[n.id] ? newCounts?.[NEW_COUNT_FOR_NAV[n.id]] ?? 0 : 0;
            return (
              <React.Fragment key={n.id}>
                {n.divider && <div className="h-px bg-g100 mx-4 my-2" />}
                <NavLink
                  to={path}
                  onClick={(e) => confirmLeave(e, path)}
                  className={
                    'relative flex items-center gap-3 w-full px-4 py-[11px] rounded-xl text-sm transition-all' +
                    (n.sub ? ' pl-11 text-[13px]' : '') +
                    (isActive
                      ? ' bg-brand-light text-brand font-bold'
                      : ' text-g700 font-medium hover:bg-g50')
                  }
                >
                  {isActive && (
                    <span
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded bg-brand"
                      aria-hidden
                    />
                  )}
                  <Icon
                    name={n.icon}
                    size={n.sub ? 16 : 20}
                    color={isActive ? 'var(--brand)' : 'currentColor'}
                  />
                  <span>{n.label}</span>
                  {newCount > 0 && (
                    <span
                      className="ml-auto flex items-center gap-1.5 text-xs font-bold text-brand"
                      aria-label={`${newCount} new`}
                    >
                      <span className="w-2 h-2 rounded-full bg-brand" aria-hidden />
                      {newCount > 99 ? '99+' : newCount}
                    </span>
                  )}
                </NavLink>
              </React.Fragment>
            );
          })}
        </nav>

        <div className="p-3 border-t border-g100">
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-g50">
            <div className="w-9 h-9 rounded-[10px] bg-brand flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
              {user ? initials(user.name) : 'AD'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold leading-tight truncate">
                {user?.name ?? 'Admin User'}
              </div>
              <div className="text-xs text-g500 truncate">
                {user?.role ?? 'Super Admin'}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-1.5 rounded-md hover:bg-g100 transition-colors"
              title="Sign out"
              aria-label="Sign out"
            >
              <Icon name="log-out" size={16} color="var(--g400)" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col overflow-hidden bg-canvas">
        <header className="flex justify-between items-center px-8 py-4 bg-white border-b border-g100 shadow-[0_1px_4px_rgba(0,0,0,0.02)]">
          <div>
            <h1 className="text-[22px] font-extrabold tracking-tight m-0">
              {meta.title}
            </h1>
            <p className="text-[13px] text-g500 mt-0.5">{meta.subtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            <div ref={notifRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="w-[42px] h-[42px] rounded-xl bg-g50 border border-g200 flex items-center justify-center relative"
                aria-label="Notifications"
                onClick={openNotifications}
                style={{ cursor: 'pointer' }}
              >
                <Icon name="bell" size={20} color="var(--g600)" />
                {unreadCount > 0 && (
                  <span
                    className="absolute top-2 right-2 w-2 h-2 rounded-full bg-brand"
                    style={{ border: '2px solid #fff' }}
                  />
                )}
              </button>

              {/* Dropdown panel anchored below the bell */}
              {notifOpen && (
                <div
                  role="dialog"
                  aria-label="Notifications"
                  style={{
                    position: 'absolute',
                    top: 50,
                    right: 0,
                    width: 380,
                    maxWidth: 'calc(100vw - 48px)',
                    background: '#fff',
                    borderRadius: 16,
                    border: '1px solid var(--g100)',
                    boxShadow: '0 16px 40px rgba(0,0,0,.14)',
                    zIndex: 200,
                    overflow: 'hidden',
                    animation: 'fadeIn .15s ease both',
                  }}
                >
                  <div
                    className="flex justify-between items-center"
                    style={{
                      padding: '14px 18px',
                      borderBottom: '1px solid var(--g100)',
                    }}
                  >
                    <span className="font-bold text-ink" style={{ fontSize: 14.5 }}>
                      Notifications
                    </span>
                    {unreadCount > 0 && !notifLoading && (
                      <button
                        onClick={handleMarkAllRead}
                        className="font-semibold hover:underline"
                        style={{ fontSize: 12, color: 'var(--brand)' }}
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>

                  <div style={{ maxHeight: 380, overflowY: 'auto', padding: '4px 18px' }}>
                    {notifLoading ? (
                      <div
                        className="py-8 text-center text-g500"
                        style={{ fontSize: 13 }}
                      >
                        Loading notifications...
                      </div>
                    ) : notifications.length === 0 ? (
                      <div
                        className="flex flex-col items-center py-8 text-center"
                        style={{ color: 'var(--g500)' }}
                      >
                        <div
                          className="rounded-2xl flex items-center justify-center mb-3"
                          style={{
                            width: 48,
                            height: 48,
                            background: 'var(--g100)',
                          }}
                        >
                          <Icon name="bell" size={22} color="var(--g400)" />
                        </div>
                        <div
                          className="font-semibold text-ink"
                          style={{ fontSize: 13.5 }}
                        >
                          No notification found
                        </div>
                        <div style={{ fontSize: 12, marginTop: 3 }}>
                          You're all caught up. New activity will show up here.
                        </div>
                      </div>
                    ) : (
                      notifications.map((n, i) => (
                        <NotificationRow
                          key={n.id}
                          notification={n}
                          showSeparator={i > 0}
                          to={resolveNotificationPath(n, 'admin')}
                          onActivate={handleNotificationClick}
                        />
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate('/admin/settings')}
              className="w-[42px] h-[42px] rounded-xl bg-g50 border border-g200 flex items-center justify-center"
              aria-label="Settings"
            >
              <Icon name="settings" size={20} color="var(--g600)" />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-8">
          <UnsavedChangesContext.Provider value={unsavedValue}>
            <Outlet />
          </UnsavedChangesContext.Provider>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
